/**
 * Native shell + storefront detection.
 *
 * Why: Apple's guideline 3.1.1 requires that digital subscriptions consumed in
 * the app go through In-App Purchase. Our community subscriptions live on the
 * web, so the native shell must hide purchase paths while still letting members
 * pray, join communities, and use everything else.
 *
 * Iter 118.26 — STOREFRONT AWARENESS. A free tier does NOT exempt us from IAP;
 * it only moves where the problem sits. And the rules are not uniform:
 *
 *   - Web / PWA          → everything allowed.
 *   - Native, US         → prices and a link out to web checkout are permitted
 *                          (the post-Epic-injunction position).
 *   - Native, elsewhere  → no price, no purchase path at all.
 *   - Native, unknown    → treated as "elsewhere". See below.
 *
 * THE DEFAULT IS DELIBERATELY CONSERVATIVE, because the two failure modes are
 * not symmetric. Showing a purchase link where it is disallowed risks an App
 * Review rejection and a shipping delay; hiding one where it was allowed costs
 * some conversion from users who can still pay on the web. So an unknown
 * storefront hides everything.
 *
 * HOW STOREFRONT ARRIVES
 * The reliable source is the native layer — StoreKit's `Storefront.countryCode`
 * on iOS — which the shell injects as `window.__GR8FUL_STOREFRONT__`.
 *
 * Deliberately NOT derived from `navigator.language` or the device locale: those
 * describe a language preference, not which App Store storefront the user's
 * account belongs to. A US-storefront user with a French locale is ordinary, and
 * guessing wrong in the permissive direction is the expensive mistake.
 *
 * Until the Capacitor shell exists and sets it, native builds take the
 * conservative path — which is the correct state to ship in.
 */

/** Storefronts where linking out to web checkout is permitted. */
const EXTERNAL_PURCHASE_STOREFRONTS = new Set(['US']);

export function isNativeContext() {
  if (typeof window === 'undefined') return false;
  try {
    // Hard override for tests, QA rigs, and a debug menu.
    if (window.__GR8FUL_NATIVE__ === true) return true;
    const cap = window.Capacitor;
    if (!cap) return false;
    if (typeof cap.isNativePlatform === 'function') {
      return !!cap.isNativePlatform();
    }
    if (typeof cap.getPlatform === 'function') {
      const p = cap.getPlatform();
      return p === 'ios' || p === 'android';
    }
    return false;
  } catch (_) {
    return false;
  }
}

/**
 * Two-letter storefront code injected by the native shell, or null when unknown.
 * Null on web is expected and irrelevant — web is unrestricted.
 */
export function getStorefront() {
  if (typeof window === 'undefined') return null;
  try {
    const sf = window.__GR8FUL_STOREFRONT__;
    return typeof sf === 'string' && sf.length === 2 ? sf.toUpperCase() : null;
  } catch (_) {
    return null;
  }
}

/**
 * Cheap boolean components can dereference directly in JSX. Snapshot at module
 * load — the shell state does not change mid-session, and grabbing it once
 * avoids re-probing `window.Capacitor` on every render.
 */
export const IS_NATIVE = isNativeContext();

/**
 * The address a link must carry when it leaves the phone.
 *
 * Inside the native shell, window.location.origin is the shell's INTERNAL
 * origin: https://localhost on Android (a dead link for anyone who taps it),
 * and https://gr8fulprayers.com on iOS only because capacitor.config.json
 * sets it that way. Share links and invite links must never be built from
 * the raw origin. On the web the page's own origin is right (that keeps
 * preview / staging domains pointing at themselves).
 */
export const PUBLIC_ORIGIN =
  process.env.REACT_APP_PUBLIC_ORIGIN || 'https://gr8fulprayers.com';

export function publicOrigin() {
  if (IS_NATIVE || typeof window === 'undefined') return PUBLIC_ORIGIN;
  return window.location.origin;
}

/**
 * May this build show prices and route the user to web checkout?
 *
 * EVERY billing surface should gate on this rather than on `!IS_NATIVE`, so the
 * storefront rule lives in one place. `IS_NATIVE` stays exported for non-billing
 * native branches (deep links, permission prompts).
 */
export const BILLING_UI_ALLOWED =
  !IS_NATIVE || EXTERNAL_PURCHASE_STOREFRONTS.has(getStorefront());

/** Copy shown wherever a paid CTA would otherwise be. */
export const NATIVE_BILLING_NOTICE_COPY =
  'Billing is managed by your group administrator on the web.';
